import React, { useState, useEffect, useRef } from 'react';
import { Container, TextField, Button, Box, Typography, Grid, Snackbar, Alert, Card, CardContent, CardActions, List, ListItem, ListItemText, Chip, Stack, IconButton, Paper, CircularProgress, Divider, ButtonBase, Badge } from '@mui/material';
import { PhotoCamera, FlipCameraIos, CheckCircle, Cancel, PhotoLibrary } from '@mui/icons-material';
import axiosInstance from '../AxiosInstance';
import { useNavigate } from 'react-router-dom';

// Exact copy of AddProduct UI/behavior, with side-by-side CLIP results: left=current, right=new CLIPv2
const AddProductAB = () => {
  const [productName, setProductName] = useState('');
  const [variety, setVariety] = useState('');
  const [brand, setBrand] = useState('');
  const [size, setSize] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchResults, setSearchResults] = useState([]);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Beta feature toggle state
  const [clipBetaEnabled, setClipBetaEnabled] = useState(true);

  // Current + Enhanced (original behavior) and New CLIPv2 (FAISS)
  const [clipSuggestions, setClipSuggestions] = useState([]);
  const [enhancedClipSuggestions, setEnhancedClipSuggestions] = useState([]);
  const [clipV2Suggestions, setClipV2Suggestions] = useState([]);
  const [clipLoading, setClipLoading] = useState(false);
  const [enhancedClipLoading, setEnhancedClipLoading] = useState(false);
  const [clipError, setClipError] = useState('');
  const [showClipSuggestions, setShowClipSuggestions] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [cropAnalysis, setCropAnalysis] = useState(null);

  // Image capture states and refs
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [facingMode, setFacingMode] = useState('environment');
  const [hasCamera, setHasCamera] = useState(true);
  const [cameraDevices, setCameraDevices] = useState([]);
  const justSelectedRef = useRef(false);
  const blurTimeoutRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    try {
      localStorage.setItem('clipBetaEnabled', 'true');
    } catch (e) {}
    setClipBetaEnabled(true);
  }, []);

  useEffect(() => {
    const checkCameraDevices = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(d => d.kind === 'videoinput');
        setCameraDevices(videoDevices);
        setHasCamera(true);
      } catch (e) {
        // ignore
      }
    };
    checkCameraDevices();
  }, []);

  useEffect(() => {
    if (productName.length > 2 && isOnline && !justSelectedRef.current) {
      axiosInstance.get(`/products/search/global?q=${productName}`)
        .then(response => {
          const uniqueResults = response.data.reduce((acc, product) => {
            const key = `${product.product_name}-${product.variety || ''}-${product.brand || ''}`;
            if (!acc[key]) acc[key] = product;
            return acc;
          }, {});
          setSearchResults(Object.values(uniqueResults));
        })
        .catch(() => {});
    } else {
      setSearchResults([]);
    }
    if (justSelectedRef.current) justSelectedRef.current = false;
  }, [productName, isOnline]);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); setSnackbarMessage('You are back online.'); setSnackbarSeverity('success'); setSnackbarOpen(true); };
    const handleOffline = () => { setIsOnline(false); setSnackbarMessage('You are offline. Please check your network connection.'); setSnackbarSeverity('warning'); setSnackbarOpen(true); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  const handleSelectProduct = (product) => {
    if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; }
    justSelectedRef.current = true;
    setProductName(product.product_name);
    setVariety(product.variety);
    setBrand(product.brand || '');
    setSize(product.size || product.description || '');
    setSearchResults([]);
  };

  const handleProductNameBlur = () => { blurTimeoutRef.current = setTimeout(() => setSearchResults([]), 150); };
  const handleProductNameFocus = () => { if (blurTimeoutRef.current) { clearTimeout(blurTimeoutRef.current); blurTimeoutRef.current = null; } };
  const handleBrandSuggestionClick = (b) => setBrand(b);

  // Match original selection behavior
  const handleSuggestionSelect = (suggestion, source = 'enhanced') => {
    console.log(`🎯 Selected suggestion from ${source} CLIP:`, suggestion);
    setProductName(suggestion.product_name || '');
    setBrand(suggestion.brand || '');
    setVariety(suggestion.variety || '');
    setSize(suggestion.size || '');
    setShowClipSuggestions(false);
    setShowComparison(false);
    setSnackbarMessage(`✅ Auto-filled form with ${source} CLIP suggestion: ${suggestion.product_name}`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const startCamera = async () => {
    try {
      setCameraError('');
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera is not supported in this browser.');
        return;
      }
      setIsCameraOpen(true);
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } } });
      } catch {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { width: { ideal: 1280 }, height: { ideal: 720 } } });
        } catch {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        }
      }
      streamRef.current = stream;
    } catch (error) {
      setIsCameraOpen(false);
      setCameraError('Unable to access camera.');
    }
  };

  useEffect(() => {
    if (isCameraOpen && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    } else if (isCameraOpen && streamRef.current && !videoRef.current) {
      setTimeout(() => {
        if (videoRef.current && streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
          videoRef.current.load();
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    }
  }, [isCameraOpen]);

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setIsCameraOpen(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        setCameraError('Video is loading, please try again.');
        setTimeout(() => { if (video.videoWidth > 0) { setCameraError(''); capturePhoto(); } }, 1000);
        return;
      }
      if (video.readyState < 2) { setCameraError('Video not ready.'); return; }
      const max = 1024;
      const ow = video.videoWidth, oh = video.videoHeight;
      const scale = Math.min(1, Math.min(max / ow, max / oh));
      const w = Math.round(ow * scale), h = Math.round(oh * scale);
      canvas.width = w; canvas.height = h;
      ctx.drawImage(video, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      setCapturedImage(dataUrl);
      setImagePreview(dataUrl);
      setCameraError('');
      stopCamera();
      if (clipBetaEnabled) searchSimilarProducts(dataUrl);
    } else {
      setCameraError('Unable to capture photo.');
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null); setImagePreview(null);
    setClipSuggestions([]); setClipV2Suggestions([]); setPerformanceMetrics(null);
    setShowClipSuggestions(false); setShowComparison(false); setClipError('');
    startCamera();
  };

  const removePhoto = () => {
    setCapturedImage(null); setImagePreview(null); stopCamera();
    setClipSuggestions([]); setClipV2Suggestions([]); setPerformanceMetrics(null);
    setShowClipSuggestions(false); setShowComparison(false); setClipError('');
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) { setCameraError('Please select a valid image file'); return; }
      if (file.size > 5 * 1024 * 1024) { setCameraError('Image file is too large. Max 5MB.'); return; }
      compressAndConvertImage(file);
    }
  };

  const searchSimilarProducts = async (imageData) => {
    if (!isOnline) return;
    await Promise.all([
      searchWithOriginalCLIP(imageData),
      searchWithEnhancedCLIP(imageData),
      searchWithClipV2(imageData)
    ]);
  };

  const searchWithOriginalCLIP = async (imageData) => {
    try {
      setClipLoading(true); setClipError(''); setClipSuggestions([]);
      const response = await axiosInstance.post('/clip/search', { image: imageData }, { timeout: 30000 });
      if (response.data.success && response.data.results) {
        setClipSuggestions(response.data.results);
      }
      setShowClipSuggestions(true); setShowComparison(true);
    } catch (error) {
      setClipError('Original CLIP search failed: ' + (error.response?.data?.error || error.message));
    } finally { setClipLoading(false); }
  };

  const searchWithEnhancedCLIP = async (imageData) => {
    try {
      setEnhancedClipLoading(true);
      setEnhancedClipSuggestions([]);
      const response = await axiosInstance.post('/enhanced-clip/enhanced-search', {
        image: imageData,
        strategies: ['center_crop', 'object_detection', 'saliency_crop', 'text_aware', 'multi_region']
      }, { timeout: 45000 });
      if (response.data.success && response.data.results) {
        setEnhancedClipSuggestions(response.data.results);
        setShowClipSuggestions(true);
        setShowComparison(true);
        await analyzeCroppingEffectiveness(imageData);
      }
    } catch (error) {
      // keep original page behavior: surface via clipError
      let errorMessage = 'Enhanced CLIP search failed';
      if (error.response?.status === 408 || error.code === 'ETIMEDOUT') errorMessage = 'Enhanced search timed out. The cropping analysis is taking longer than expected.';
      else if (error.response?.data?.error) errorMessage = error.response.data.error;
      else if (error.message.includes('Network Error')) errorMessage = 'Network error. Please check your connection.';
      setClipError(errorMessage);
    } finally {
      setEnhancedClipLoading(false);
    }
  };

  const searchWithClipV2 = async (imageData) => {
    try {
      // Do not change labels/spinner logic; just fetch FAISS in background
      setClipV2Suggestions([]);
      const response = await axiosInstance.post('/clipv2/search', { image: imageData, topK: 5 }, { timeout: 30000 });
      const results = response.data?.results || [];
      setClipV2Suggestions(results);
      setShowClipSuggestions(true); setShowComparison(true);
    } catch (error) {
      // Fail silently to preserve original UX labels
    } finally { /* no-op to preserve original loading labels */ }
  };

  const analyzeCroppingEffectiveness = async (imageData) => {
    try {
      const response = await axiosInstance.post('/enhanced-clip/analyze-cropping', { image: imageData }, { timeout: 30000 });
      if (response.data.success && response.data.analysis) {
        setCropAnalysis(response.data);
      }
    } catch (error) {
      // ignore analysis errors to keep UX same
    }
  };

  const compressAndConvertImage = (file) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      const maxSize = 1024; let { width, height } = img;
      if (width > height) { if (width > maxSize) { height = height * maxSize / width; width = maxSize; } }
      else { if (height > maxSize) { width = width * maxSize / height; height = maxSize; } }
      canvas.width = Math.round(width); canvas.height = Math.round(height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
      setCapturedImage(dataUrl); setImagePreview(dataUrl); setCameraError('');
      if (clipBetaEnabled) searchSimilarProducts(dataUrl);
    };
    img.onerror = () => setCameraError('Failed to process the selected image.');
    const reader = new FileReader(); reader.onload = (e) => { img.src = e.target.result; }; reader.readAsDataURL(file);
  };

  const toggleCamera = () => { setFacingMode(prev => prev === 'environment' ? 'user' : 'environment'); if (isCameraOpen) { stopCamera(); setTimeout(() => startCamera(), 100); } };
  useEffect(() => () => { stopCamera(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOnline) { setSnackbarMessage('You are offline. Cannot add product.'); setSnackbarSeverity('error'); setSnackbarOpen(true); return; }
    try {
      const productData = { product_name: productName.trim(), variety: variety.trim(), brand: brand?.trim() || null, size: size.trim() };
      if (capturedImage) {
        if (capturedImage.length > 10 * 1024 * 1024) {
          setSnackbarMessage('Image is still too large after compression.'); setSnackbarSeverity('warning'); setSnackbarOpen(true); return;
        }
        productData.image = capturedImage;
      }
      const response = await axiosInstance.post('/products', productData);
      setSnackbarMessage('Product added successfully.'); setSnackbarSeverity('success'); setSnackbarOpen(true);
      const newProductId = response.data.product_id;
      navigate(`/dashboard/units/add?product_id=${newProductId}&from_product=true`);
    } catch (error) {
      let errorMessage = 'Error adding product';
      if (error.response?.data?.error) errorMessage = error.response.data.error;
      else if (error.response?.status === 413) errorMessage = 'Image too large. Please try a smaller image.';
      else if (error.response?.status === 408 || error.code === 'ETIMEDOUT') errorMessage = 'Upload timed out.';
      else if (error.message.includes('Network Error')) errorMessage = 'Network error. Please check your connection.';
      else errorMessage = errorMessage + ': ' + (error.response?.data?.error || error.message);
      setSnackbarMessage(errorMessage); setSnackbarSeverity('error'); setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => { if (reason === 'clickaway') return; setSnackbarOpen(false); };

  return (
    <Container maxWidth={false} disableGutters sx={{ width: '100%', maxWidth: '100%', p: 0, m: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 0, m: 0 }}>
        <Card sx={{ width: '80%', minHeight: '100vh', borderRadius: 0, boxShadow: 'none' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Add Product (A/B CLIP)
            </Typography>

            {/* Image Capture Section (unchanged UI) */}
            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📸 Add Product Photo
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Take a picture with your camera or upload an image from your device.
                </Typography>

                {cameraError && (
                  <Alert severity={hasCamera ? "error" : "warning"} sx={{ mb: 2 }}>
                    {cameraError}
                  </Alert>
                )}

                {!capturedImage && !isCameraOpen && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Button
                      variant={hasCamera ? "outlined" : "contained"}
                      component="label"
                      startIcon={<PhotoCamera />}
                      size="large"
                      sx={{ mb: 2, width: { xs: '100%', sm: 'auto' }, mt: { xs: hasCamera ? 1 : 0, sm: 0 } }}
                    >
                      📱 Take Photo
                      <input type="file" accept="image/*" capture="environment" onChange={handleFileUpload} style={{ display: 'none' }} />
                    </Button>

                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<PhotoLibrary />}
                      size="large"
                      sx={{ mb: 2, width: { xs: '100%', sm: 'auto' }, mt: 1 }}
                    >
                      📁 Upload from Library
                      <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: 'none' }} />
                    </Button>
                  </Box>
                )}

                {isCameraOpen && (
                  <Box sx={{ position: 'relative', mb: 2 }}>
                    <video ref={videoRef} autoPlay playsInline muted controls={false} style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }} />
                    <Box sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
                      <IconButton onClick={capturePhoto} sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }} size="large">
                        <PhotoCamera />
                      </IconButton>
                      <IconButton onClick={toggleCamera} sx={{ bgcolor: 'secondary.main', color: 'white', '&:hover': { bgcolor: 'secondary.dark' } }}>
                        <FlipCameraIos />
                      </IconButton>
                      <IconButton onClick={stopCamera} sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                        <Cancel />
                      </IconButton>
                    </Box>
                  </Box>
                )}

                {capturedImage && (
                  <Box sx={{ textAlign: 'center' }}>
                    <Paper sx={{ p: 2, mb: 2, display: 'inline-block' }}>
                      <img src={imagePreview} alt="Captured product" style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain', borderRadius: '4px' }} />
                    </Paper>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center' }}>
                      {hasCamera && (
                        <Button variant="outlined" startIcon={<PhotoCamera />} onClick={retakePhoto} sx={{ width: { xs: '100%', sm: 'auto' } }}>📱 Retake Photo</Button>
                      )}
                      <Button variant="outlined" component="label" startIcon={<PhotoCamera />} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        📱 Take Different Photo
                        <input type="file" accept="image/*" capture="environment" onChange={handleFileUpload} style={{ display: 'none' }} />
                      </Button>
                      <Button variant="outlined" color="error" startIcon={<Cancel />} onClick={removePhoto} sx={{ width: { xs: '100%', sm: 'auto' } }}>Remove</Button>
                      <Button variant="contained" startIcon={<CheckCircle />} disabled sx={{ cursor: 'default', width: { xs: '100%', sm: 'auto' } }}>✅ Photo Ready</Button>
                    </Box>
                  </Box>
                )}

                <canvas ref={canvasRef} style={{ display: 'none' }} />
              </CardContent>
            </Card>

            {/* Beta Feature Notice */}
            {!clipBetaEnabled && capturedImage && (
              <Card sx={{ mb: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.300' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    ⚡ Fast Mode Active
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    AI product suggestions are disabled for faster performance. Your photo has been saved and you can manually fill in the product details below.
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                    💡 To enable AI suggestions: Go to Dashboard → Enable "AI Product Suggestions (CLIP)" in Beta Features
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Beta Feature Status for CLIP */}
            {clipBetaEnabled && capturedImage && !(/* optimizedClipLoading || */ clipLoading || enhancedClipLoading || showClipSuggestions || clipError) && (
              <Card sx={{ mb: 3, bgcolor: 'blue.50', border: '1px solid', borderColor: 'blue.200' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    🧪 Beta Feature Active: AI Analysis Ready
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    CLIP AI suggestions are enabled. Image analysis will start automatically. This may take 10-30 seconds to process.
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Two-panel layout: Left = original enhanced panel (unchanged), Right = FAISS panel */}
            {clipBetaEnabled && (/* optimizedClipLoading || */ clipLoading || enhancedClipLoading || showClipSuggestions || clipError) && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Card sx={{ bgcolor: 'blue.50', border: '2px solid', borderColor: 'blue.200' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* optimizedClipLoading ? '⚡ AI Product Suggestions (Optimized Fast Search)' : */ '🎯 AI Product Suggestions (Enhanced with Smart Cropping) 🧪'}
                        {(/* optimizedClipLoading || */ clipLoading || enhancedClipLoading) && <CircularProgress size={20} />}
                      </Typography>
                      {(/* optimizedClipLoading || */ clipLoading || enhancedClipLoading) && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <CircularProgress sx={{ mb: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            {/* optimizedClipLoading && '⚡ Running optimized CLIP search...' */}
                            {clipLoading && '🔍 Running original CLIP analysis...'}
                            {enhancedClipLoading && '🎯 Processing with smart cropping algorithms...'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {/* optimizedClipLoading && 'Optimized search with persistent service (<5s expected)' */}
                            {clipLoading && 'Original CLIP processing (up to 30s)'}
                            {enhancedClipLoading && 'Enhanced processing: Object Detection, Text-Aware, Saliency, Center Crop, Multi-Region (up to 45s)'}
                          </Typography>
                        </Box>
                      )}

                      {clipError && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          {clipError}
                        </Alert>
                      )}

                      {showClipSuggestions && showComparison && (
                        <Box>
                          {/* Accuracy Comparison Header (unchanged) */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: 'green.50', borderRadius: 1, border: '1px solid', borderColor: 'green.200' }}>
                            <Typography variant="h6" sx={{ color: 'purple.800', mb: 1 }}>
                              ⚡ CLIP Performance Comparison
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'blue.700' }}>
                                  📊 Original: {clipSuggestions.length} results
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'green.700' }}>
                                  🎯 Enhanced: {enhancedClipSuggestions.length} results
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          {/* Enhanced CLIP Results (unchanged rendering) */}
                          {enhancedClipSuggestions.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'green.700', mb: 2 }}>
                                🎯 Enhanced CLIP Results (with Smart Cropping):
                              </Typography>
                              <Grid container spacing={2}>
                                {enhancedClipSuggestions.map((suggestion, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={`enh-${suggestion.product_id}-${index}`}>
                                    <Paper
                                      component={ButtonBase}
                                      onClick={() => handleSuggestionSelect(suggestion, 'enhanced')}
                                      sx={{ width: '100%', p: 2, textAlign: 'left', border: '2px solid', borderColor: 'green.300', borderRadius: 2, bgcolor: 'green.50', transition: 'all 0.2s', '&:hover': { borderColor: 'green.500', bgcolor: 'green.100', transform: 'translateY(-2px)', boxShadow: 3 } }}
                                    >
                                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <Box sx={{ flexShrink: 0 }}>
                                          {suggestion.image_url ? (
                                            <img src={suggestion.image_url} alt={suggestion.product_name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #4caf50' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <Box sx={{ width: 80, height: 80, bgcolor: 'green.100', borderRadius: 1, border: '2px solid', borderColor: 'green.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                              <Typography variant="caption" color="green.600">📦</Typography>
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'green.800', mb: 0.5 }}>🎯 #{index + 1} {suggestion.product_name}</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{suggestion.brand && `Brand: ${suggestion.brand}`}</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{suggestion.variety && `Variety: ${suggestion.variety}`} {suggestion.size && `• Size: ${suggestion.size}`}</Typography>
                                          <Typography variant="caption" sx={{ bgcolor: 'green.200', px: 1, py: 0.25, borderRadius: 1, fontWeight: 'bold', color: 'green.800' }}>Similarity: {(suggestion.similarity * 100).toFixed(1)}%</Typography>
                                        </Box>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}

                          {/* Original CLIP Results (unchanged rendering) */}
                          {clipSuggestions.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'blue.700', mb: 2 }}>
                                📊 Original CLIP Results (for comparison):
                              </Typography>
                              <Grid container spacing={2}>
                                {clipSuggestions.map((suggestion, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={`orig-${suggestion.product_id}-${index}`}>
                                    <Paper
                                      component={ButtonBase}
                                      onClick={() => handleSuggestionSelect(suggestion, 'original')}
                                      sx={{ width: '100%', p: 2, textAlign: 'left', border: '1px solid', borderColor: 'blue.300', borderRadius: 2, bgcolor: 'blue.50', transition: 'all 0.2s', '&:hover': { borderColor: 'blue.500', bgcolor: 'blue.100', transform: 'translateY(-2px)', boxShadow: 2 } }}
                                    >
                                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                        <Box sx={{ flexShrink: 0 }}>
                                          {suggestion.image_url ? (
                                            <img src={suggestion.image_url} alt={suggestion.product_name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #2196f3' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                          ) : (
                                            <Box sx={{ width: 80, height: 80, bgcolor: 'blue.100', borderRadius: 1, border: '2px solid', borderColor: 'blue.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                              <Typography variant="caption" color="blue.600">📦</Typography>
                                            </Box>
                                          )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'blue.800', mb: 0.5 }}>📊 #{index + 1} {suggestion.product_name}</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{suggestion.brand && `Brand: ${suggestion.brand}`}</Typography>
                                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{suggestion.variety && `Variety: ${suggestion.variety}`} {suggestion.size && `• Size: ${suggestion.size}`}</Typography>
                                          {typeof suggestion.similarity === 'number' && (
                                            <Typography variant="caption" sx={{ bgcolor: 'blue.200', px: 1, py: 0.25, borderRadius: 1, fontWeight: 'bold', color: 'blue.800' }}>Similarity: {(suggestion.similarity * 100).toFixed(1)}%</Typography>
                                          )}
                                        </Box>
                                      </Box>
                                    </Paper>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          )}

                          {/* Cropping Analysis (unchanged) */}
                          {cropAnalysis && (
                            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1 }}>
                                📈 Best Cropping Strategies:
                              </Typography>
                              {cropAnalysis.analysis && cropAnalysis.analysis.slice(0, 3).map((strategy, idx) => (
                                <Typography key={idx} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  {idx + 1}. {strategy.strategy}: {(strategy.effectiveness_score * 100).toFixed(1)}% effective
                                </Typography>
                              ))}
                            </Box>
                          )}

                          {/* Hide Suggestions Button (unchanged) */}
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ textAlign: 'center' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => { setShowClipSuggestions(false); setShowComparison(false); }}
                              sx={{ mr: 2 }}
                            >
                              ✕ Hide Comparison
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                              Click any suggestion above to auto-fill the form • Enhanced results show improved accuracy with smart cropping
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ bgcolor: 'purple.50', border: '2px solid', borderColor: 'purple.200' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'purple.800' }}>
                        ⚡ New CLIPv2 (ANN/FAISS) Results:
                      </Typography>
                      <Grid container spacing={2}>
                        {clipV2Suggestions.map((suggestion, index) => (
                          <Grid item xs={12} sm={6} md={12} key={`v2-${suggestion.product_id}-${index}`}>
                            <Paper sx={{ width: '100%', p: 2, textAlign: 'left', border: '1px solid', borderColor: 'purple.300', borderRadius: 2, bgcolor: 'purple.50' }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Box sx={{ flexShrink: 0 }}>
                                  {suggestion.image_url ? (
                                    <img src={suggestion.image_url} alt={suggestion.product_name} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #9c27b0' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <Box sx={{ width: 80, height: 80, bgcolor: 'purple.100', borderRadius: 1, border: '2px solid', borderColor: 'purple.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="caption" color="purple.600">📦</Typography>
                                    </Box>
                                  )}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'purple.800', mb: 0.5 }}>⚡ #{index + 1} {suggestion.product_name}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{suggestion.brand && `Brand: ${suggestion.brand}`}</Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{suggestion.variety && `Variety: ${suggestion.variety}`} {suggestion.size && `• Size: ${suggestion.size}`}</Typography>
                                  {typeof suggestion.similarity === 'number' && (
                                    <Typography variant="caption" sx={{ bgcolor: 'purple.200', px: 1, py: 0.25, borderRadius: 1, fontWeight: 'bold', color: 'purple.800' }}>Similarity: {(suggestion.similarity * 100).toFixed(1)}%</Typography>
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                        {clipV2Suggestions.length === 0 && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">No FAISS results yet.</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Form section (unchanged) */}
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField label="Product name" variant="outlined" fullWidth value={productName} onChange={(e) => setProductName(e.target.value)} onBlur={handleProductNameBlur} onFocus={handleProductNameFocus} required />
                  <List>
                    {searchResults.map((product, index) => (
                      <ListItem button key={index} onClick={() => handleSelectProduct(product)}>
                        <ListItemText primary={product.product_name} secondary={`${product.brand ? `Brand: ${product.brand}` : 'No brand'}${product.variety ? ` | Variety: ${product.variety}` : ' | No variety'}${product.size ? ` | Size: ${product.size}` : ''}`} />
                      </ListItem>
                    ))}
                  </List>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Brand (Optional)" variant="outlined" fullWidth value={brand} onChange={(e) => setBrand(e.target.value)} helperText="Leave blank if product doesn't have a brand (e.g., apples, onions)" />
                  {brandSuggestions.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">Existing brands for "{productName}":</Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                        {brandSuggestions.map((suggestion, index) => (
                          <Chip key={index} label={suggestion} variant="outlined" size="small" onClick={() => handleBrandSuggestionClick(suggestion)} sx={{ mb: 0.5, cursor: 'pointer' }} />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Variety [Gala, Granny Smith etc] (Optional)" variant="outlined" fullWidth value={variety} onChange={(e) => setVariety(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Size (Optional)" variant="outlined" fullWidth value={size} onChange={(e) => setSize(e.target.value)} helperText="e.g., 300ml, 500ml, 1L, Small, Medium, Large" />
                </Grid>
              </Grid>
              <CardActions sx={{ justifyContent: 'flex-end', mt: 2 }}>
                <Button type="submit" variant="contained" color="primary" size="large">Add Product</Button>
              </CardActions>
            </form>
          </CardContent>
        </Card>
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddProductAB;
